/**
 * Oxbo-P client — the domain operational record (events, daily reports, cost,
 * NPT, plan-vs-actual, inbox, saved queries). Read-through cache; Oxbo-Z owns
 * none of it and never recomputes a KPI — every computed number (days,
 * pct_complete, rop_avg, variance, npt roll-ups) is served by P.
 *
 * Base URL is VITE_OXBO_P_URL (p.dev.oxbo.energy by default). MSW serves mocks
 * in standalone mode. Endpoints mirror the live contract exactly.
 */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getBrokerToken, isGlobalAdmin } from "./brokerAuth";
import type {
  ActivityOut,
  EventOut,
  DailyReportOut,
  DailyReportCreate,
  DailyReportUpdate,
  ReportCloseResponse,
  CostPivotResponse,
  CostPivotDimension,
  NptRollupResponse,
  PlanVsActualResponse,
  AfeLineGridResponse,
  WellSurveysResponse,
  EventSafetyResponse,
  BhaRunsResponse,
  InboxResponse,
  SavedQueryOut,
  MudChecksResponse,
  TubularsResponse,
} from "./types";

const P_BASE_URL = import.meta.env.VITE_OXBO_P_URL ?? "https://p.dev.oxbo.energy";

export interface InboxParams {
  date_from?: string;
  date_to?: string;
  well_uwi?: string;
  state?: string;
  limit?: number;
}

export const pApi = createApi({
  reducerPath: "pApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${P_BASE_URL}/v1`,
    // In the RCI-Auth broker prod build Oxbo-P verifies the same RS256 access
    // token against the broker JWKS, so attach it as Bearer. In dev / mocks
    // builds there is no broker token and no Authorization header is sent
    // (P historically requires none), so this is a safe superset.
    prepareHeaders: (headers) => {
      const token = getBrokerToken();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      if (isGlobalAdmin()) headers.set("X-OXBO-Admin-Mode", "true");
      return headers;
    },
  }),
  // Operational data is more volatile than master data — shorter cache window.
  keepUnusedDataFor: 60,
  tagTypes: ["Event", "Report", "Activity", "Tubulars", "Inbox", "SavedQuery"],
  endpoints: (builder) => ({
    // ── Events (jobs) ─────────────────────────────────────────────────────
    // Tenant-wide bulk pull — every event across every well. Used by the Well
    // Explorer to badge wells that have operational activity. One fetch,
    // grouped client-side by well_uwi.
    getAllEvents: builder.query<EventOut[], void>({
      query: () => "/events",
      providesTags: (result) =>
        result
          ? [
              ...result.map((ev) => ({ type: "Event" as const, id: ev.id })),
              { type: "Event" as const, id: "ALL" },
            ]
          : [{ type: "Event" as const, id: "ALL" }],
    }),
    getWellEvents: builder.query<EventOut[], string>({
      query: (wellUwi) => `/wells/${encodeURIComponent(wellUwi)}/events`,
      providesTags: (result, _e, wellUwi) =>
        result
          ? [
              ...result.map((ev) => ({ type: "Event" as const, id: ev.id })),
              { type: "Event" as const, id: `WELL-${wellUwi}` },
            ]
          : [{ type: "Event" as const, id: `WELL-${wellUwi}` }],
    }),

    // ── Surveys (directional passes + stations) ───────────────────────────
    // WELL-scoped (by uwi), not event-scoped: a well's directional survey
    // passes each with their active stations (+ any ungrouped legacy stations).
    // Display-only; tvd/dls are compute-server-derived and served, not computed
    // here.
    getWellSurveys: builder.query<WellSurveysResponse, string>({
      query: (uwi) => `/wells/${encodeURIComponent(uwi)}/surveys`,
    }),

    // ── Time log (activities) ─────────────────────────────────────────────
    // The event's time-log rows for the Time/NPT screen, ordered day → seq →
    // time by the server. Display-only; the FE never sums or classifies NPT.
    getEventActivities: builder.query<ActivityOut[], string>({
      query: (eventId) => `/events/${eventId}/activities`,
      providesTags: (result, _e, eventId) =>
        result
          ? [
              ...result.map((a) => ({ type: "Activity" as const, id: a.id })),
              { type: "Activity" as const, id: `EVENT-${eventId}` },
            ]
          : [{ type: "Activity" as const, id: `EVENT-${eventId}` }],
    }),

    // ── Tubulars (assemblies / casing / pipe-tally) ───────────────────────
    // The event's assemblies, casing strings and pipe-tally rows for the
    // Tubulars screen (§10.2), ordered by the server. Display-only.
    getEventTubulars: builder.query<TubularsResponse, string>({
      query: (eventId) => `/events/${eventId}/tubulars`,
      providesTags: (_r, _e, eventId) => [{ type: "Tubulars", id: `EVENT-${eventId}` }],
    }),

    // ── Daily reports ─────────────────────────────────────────────────────
    getEventReports: builder.query<DailyReportOut[], string>({
      query: (eventId) => `/events/${eventId}/reports`,
      providesTags: (result, _e, eventId) =>
        result
          ? [
              ...result.map((r) => ({ type: "Report" as const, id: r.id })),
              { type: "Report" as const, id: `EVENT-${eventId}` },
            ]
          : [{ type: "Report" as const, id: `EVENT-${eventId}` }],
    }),
    getReport: builder.query<DailyReportOut, string>({
      query: (reportId) => `/reports/${reportId}`,
      providesTags: (_r, _e, id) => [{ type: "Report", id }],
    }),
    createReport: builder.mutation<DailyReportOut, DailyReportCreate>({
      query: (body) => ({ url: "/reports", method: "POST", body }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: "Report", id: `EVENT-${result.event_id}` },
              { type: "Inbox", id: "LIST" },
            ]
          : [{ type: "Inbox", id: "LIST" }],
    }),
    updateReport: builder.mutation<DailyReportOut, { id: string; body: DailyReportUpdate }>({
      query: ({ id, body }) => ({ url: `/reports/${id}`, method: "PATCH", body }),
      invalidatesTags: (result, _e, { id }) =>
        result
          ? [
              { type: "Report", id },
              { type: "Report", id: `EVENT-${result.event_id}` },
            ]
          : [{ type: "Report", id }],
    }),
    closeReport: builder.mutation<ReportCloseResponse, { id: string; isLastOfEvent?: boolean }>({
      query: ({ id, isLastOfEvent }) => ({
        url: `/reports/${id}/close`,
        method: "POST",
        body: { is_last_of_event: isLastOfEvent ?? false },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Report", id },
        { type: "Inbox", id: "LIST" },
      ],
    }),

    // ── Server-computed roll-ups (display-only; never recomputed on the FE) ─
    getCostPivot: builder.query<
      CostPivotResponse,
      { eventId: string; dimension?: CostPivotDimension }
    >({
      query: ({ eventId, dimension }) => ({
        url: `/events/${eventId}/cost-pivot`,
        params: dimension ? { dimension } : {},
      }),
    }),
    getPlanVsActual: builder.query<PlanVsActualResponse, string>({
      query: (eventId) => `/events/${eventId}/plan-vs-actual`,
    }),
    // The AFE Management line grid (§10.7): active line versions with the
    // accrual (devengado) + live plan-vs-actual variance per line + roll-up
    // totals. Server-computed; the FE only formats and lays out.
    getAfeLines: builder.query<AfeLineGridResponse, string>({
      query: (eventId) => `/events/${eventId}/afe-lines`,
    }),
    getNptRollup: builder.query<NptRollupResponse, string>({
      query: (eventId) => `/events/${eventId}/npt-rollup`,
    }),
    getEventSafety: builder.query<EventSafetyResponse, string>({
      query: (eventId) => `/events/${eventId}/safety`,
    }),

    // Mud & fluids — the event's product inventory + product-transaction trend.
    // Display-only; the FE only formats and lays out (no client-side rollups).
    getEventMudChecks: builder.query<MudChecksResponse, string>({
      query: (eventId) => `/events/${eventId}/mud-checks`,
    }),

    // BHA runs + their bit ops + the assembly (BHA metadata) each run pulled.
    // Server-assembled (§10.3); the FE only lays it out.
    getEventBhaRuns: builder.query<BhaRunsResponse, string>({
      query: (eventId) => `/events/${eventId}/bha-runs`,
    }),

    // ── Inbox / saved queries (cross-well) ────────────────────────────────
    getInbox: builder.query<InboxResponse, InboxParams | void>({
      query: (params) => ({ url: "/inbox", params: params ?? {} }),
      providesTags: [{ type: "Inbox", id: "LIST" }],
    }),
    getSavedQueries: builder.query<SavedQueryOut[], void>({
      query: () => "/saved-queries",
      providesTags: [{ type: "SavedQuery", id: "LIST" }],
    }),
  }),
});

export const {
  useGetAllEventsQuery,
  useGetWellEventsQuery,
  useGetWellSurveysQuery,
  useGetEventActivitiesQuery,
  useGetEventTubularsQuery,
  useGetEventReportsQuery,
  useGetReportQuery,
  useCreateReportMutation,
  useUpdateReportMutation,
  useCloseReportMutation,
  useGetCostPivotQuery,
  useGetPlanVsActualQuery,
  useGetAfeLinesQuery,
  useGetNptRollupQuery,
  useGetEventMudChecksQuery,
  useGetEventSafetyQuery,
  useGetEventBhaRunsQuery,
  useGetInboxQuery,
  useGetSavedQueriesQuery,
} = pApi;
